import * as React from 'react';
import {
  Button,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Label,
  Alert,
} from 'reactstrap';

import styled from 'styled-components';
import { showKeyModal } from '../../operations/showKeyModal';
import { OktaAuth } from '@okta/okta-auth-js';
import * as Model from '../../models';
import { useDispatch } from 'react-redux';
import { InfoIcon } from '@primer/octicons-react';
import { useTheme } from '../../styles/useTheme';
import { TextField } from '../TextField/TextField';
import Autocomplete from '@mui/material/Autocomplete';
import { CircularProgress } from '@mui/material';
import Popper from '@mui/material/Popper';

// Input sanitization utilities
const sanitizeInput = (input: string): string => {
  if (!input) return '';
  
  // Remove potentially dangerous characters and HTML
  return input
    .replace(/[<>'"&]/g, '') // Remove HTML special characters
    .replace(/javascript:/gi, '') // Remove javascript: protocol
    .replace(/data:/gi, '') // Remove data: protocol
    .replace(/vbscript:/gi, '') // Remove vbscript: protocol
    .trim(); // Remove leading/trailing whitespace
};

const sanitizeChangeNumber = (input: string): string => {
  if (!input) return '';
  
  // Allow only alphanumeric characters and common change request formats
  return input
    .replace(/[^A-Za-z0-9\-_]/g, '')
    .toUpperCase()
    .trim()
    .substring(0, 20); // Limit length
};

const sanitizeDescription = (input: string): string => {
  if (!input) return '';
  
  // More permissive but still safe sanitization for descriptions
  return input
    .replace(/[<>'"]/g, '') // Remove HTML special characters but allow other punctuation
    .replace(/javascript:/gi, '')
    .replace(/data:/gi, '')
    .replace(/vbscript:/gi, '')
    .substring(0, 500); // Limit length to reasonable description size
};

// Validation functions
const isValidChangeNumber = (changeNumber: string): boolean => {
  if (!changeNumber) return false;
  // Check if it matches common change request patterns (CHG followed by numbers)
  const changePattern = /^CHG\d{7,10}$/i;
  return changePattern.test(changeNumber);
};

const isValidDescription = (description: string): boolean => {
  return description.trim().length >= 10 && description.trim().length <= 500;
};

interface Component {
  inputValue?: string;
  name?: string;
  number: string;
}

export interface DispatchProps {
  getKeys: (
    auth: OktaAuth,
    account: Model.Account,
    role: Model.Role,
    duration: number
  ) => void;
}

// categories for the dropdown
// const CATEGORIES = [
//   '3rd Party',
//   'Code Deploy',
//   'Configuration',
//   'Data',
//   'Decomission',
//   'Feature toggle',
//   'Fix/Repair',
//   'Infrastructure',
//   'Maintenance',
//   'Monitoring',
//   'Patching',
//   'SW/HW Installation',
//   'SW/HW Upgrade',
// ];
const CATEGORIES = [
  'Manual Deployment of Code or Infrastructure',
  'Configuration Change',
  'Feature Toggle Change',
  'Operational Task',
  'Data Management'
];


const ChangeModal = ({ closeModal, account, role, duration, auth, url }) => {
  const [changeNumber, setChangeNumber] = React.useState('');
  const [primaryCINumber, setPrimaryCINumber] = React.useState('');
  const [description, setDescription] = React.useState('');
  // useState for selected category
  const [category, setCategory] = React.useState('');
  const [open, setOpen] = React.useState(false);
  const [options, setOptions] = React.useState<readonly Component[]>([]);
  const [loading, setLoading] = React.useState(false);
  const [validationErrors, setValidationErrors] = React.useState({
    changeNumber: '',
    description: '',
  });



  const [theme] = useTheme();

  const dispatch = useDispatch();
  const env = process.env.REACT_APP_ENVIRONMENT;

  const handleOpen = () => {
    setOpen(true);
    (async () => {
      setLoading(true);
      try {
        let response = await fetch('/data/components.json');
        if (env === 'local') {
          response = await fetch('components.json');
        }
        if (response.ok) {
          const buffer = await response.arrayBuffer();
          const text = new TextDecoder().decode(buffer);
          const data = JSON.parse(text);
          
          // Sanitize the fetched data
          const sanitizedData = data.map((component: Component) => ({
            ...component,
            name: component.name ? sanitizeInput(component.name) : '',
            number: component.number ? sanitizeInput(component.number) : '',
          }));
          
          setOptions(sanitizedData);
        }
      } catch (error) {
        console.error('Error fetching components:', error);
      }
      setLoading(false);
    })();
  };

  const handleClose = () => {
    setOpen(false);
    setOptions([]);
  };

  const handleSubmit = React.useCallback(async () => {
    // Sanitize all inputs before submission
    const sanitizedChangeNumber = sanitizeChangeNumber(changeNumber);
    const sanitizedPrimaryCINumber = sanitizeInput(primaryCINumber);
    const sanitizedDescription = sanitizeDescription(description);
    const sanitizedCategory = sanitizeInput(category);
    
    // if a url is provided to the change modal, then we know we are coming from the "Console" button
    if (url) {
      if (sanitizedChangeNumber) {
        window.open(`${url}&changeNumber=${encodeURIComponent(sanitizedChangeNumber)}`);
      }
      if (sanitizedPrimaryCINumber) {
        window.open(`${url}&primaryCINumber=${encodeURIComponent(sanitizedPrimaryCINumber)}&description="${encodeURIComponent(sanitizedDescription)}"&category="${encodeURIComponent(sanitizedCategory)}"`);
      }
      closeModal();
    } else {
      // if no url is provided, then we are coming from the "Get Keys" button/dropdown, so we need to show the Key Modal
      dispatch(
        showKeyModal(
          auth,
          account,
          role,
          duration,
          sanitizedChangeNumber,
          sanitizedPrimaryCINumber,
          sanitizedDescription,
          sanitizedCategory
        )
      );
    }
  }, [url, changeNumber, primaryCINumber, closeModal, description, category, dispatch, auth, account, role, duration]);

  const isSubmitDisabled =
    !(
      (changeNumber && isValidChangeNumber(changeNumber)) ||
      (primaryCINumber && category && description && isValidDescription(description))
    );

  return (
    <div>
      <StyledModalHeader toggle={closeModal}>
        AWS Account Access
      </StyledModalHeader>
      <StyledModalBody>
        <div style={{ marginBottom: '1rem' }}>
          <Label for="requestedDuration" style={{ fontWeight: 'bold' }}>
            Requested Key Duration:
          </Label>
          <div style={{ padding: '0.5rem', borderRadius: '0.25rem' }}>
            {duration} hour(s)
          </div>
        </div>
        <div style={{ marginBottom: '1rem' }}>
          <Label for="requestedRole" style={{ fontWeight: 'bold' }}>
            Requested Role:
          </Label>
          <div style={{ padding: '0.5rem', borderRadius: '0.25rem' }}>
            {role.name}
          </div>
        </div>
        <StyledAlert color="warning">
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <InfoIcon size={24} />
            <div style={{ marginLeft: '1rem' }}>
              For the selected role, please provide either an existing
              ServiceNow Change Ticket # or a Component ID, Activity Type, and Description for ALKS to
              create a change ticket on your behalf.
            </div>
          </div>
        </StyledAlert>
        <Label style={{ fontWeight: 'bold' }} for="changeNumber">
          Existing Change Request <span style={{color:'red'}}>*</span>
        </Label>
        <TextField
          id="changeNumber"
          label="CHGXXXXXXX"
          variant="outlined"
          value={changeNumber}
          sx={{ backgroundColor: theme.backgroundColor, color: theme.textColor }}
          onChange={(e) => {
            const sanitized = sanitizeChangeNumber(e.target.value);
            setChangeNumber(sanitized);
            
            // Update validation errors
            if (sanitized && !isValidChangeNumber(sanitized)) {
              setValidationErrors(prev => ({
                ...prev,
                changeNumber: 'Please enter a valid change request number (e.g., CHG1234567)'
              }));
            } else {
              setValidationErrors(prev => ({
                ...prev,
                changeNumber: ''
              }));
            }
          }}
          disabled={!!primaryCINumber}
          fullWidth
          multiline={false}
          error={!!validationErrors.changeNumber}
          helperText={validationErrors.changeNumber}
        />
        <div style={{ textAlign: 'center', margin: '1rem 0' }}>
          <span>OR</span>
        </div>
        <Label style={{ fontWeight: 'bold' }} for="workloadComponentId">
          Component ID <span style={{color:'red'}}>*</span>
        </Label>
        <Autocomplete
          id="workloadComponentId"
          open={open}
          disabled={!!changeNumber}
          slotProps={{
            paper: {
              sx: {
          backgroundColor: theme.backgroundColor,
          color: theme.textColor,
          border: `1px solid ${theme.borderColor}`,
              },
            },
          }}
          options={options}
          getOptionLabel={(option) => {
            // Value selected with enter, right from the input
            if (typeof option === 'string') {
              return option;
            }
            // Add "xxx" option created dynamically
            if (option.inputValue) {
              return `${option.number}`;
            }
            // Regular option
            return `[ ${option.number} ] - ${option.name}`;
          }}
          isOptionEqualToValue={(option, value) => option.number === value.number}
          loading={loading}
          onOpen={handleOpen}
          onClose={handleClose}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Type to Search...Component ID or Name"
              variant="outlined"
              value=""
              multiline={false}
              InputProps={{
                ...params.InputProps,
                type: 'search',
                endAdornment: (
                  <React.Fragment>
                    {loading ? <CircularProgress color="inherit" size={20} /> : null}
                    {params.InputProps.endAdornment}
                  </React.Fragment>
                ),
              }}
            />
          )}

          onChange={(event, newValue) => {
            if (typeof newValue === 'string') {
              setPrimaryCINumber(sanitizeInput(newValue));
            } else {
              setPrimaryCINumber(newValue?.number ? sanitizeInput(newValue.number) : '');
            }
          }}
        />
        <div style={{marginTop: '1rem'}}>
          <Label style={{ fontWeight: 'bold' }} for="category">
          Activity Type <span style={{color:'red'}}>*</span>
          </Label>
          <Autocomplete
            id="category"
            options={CATEGORIES}
            value={category}
            disabled={!!changeNumber}
            onChange={(event, newValue) => {
              setCategory(newValue ? sanitizeInput(newValue) : '');
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Select an activity type"
                variant="outlined"
                fullWidth
                sx={{ backgroundColor: theme.backgroundColor, color: theme.textColor }}
                InputProps={{
                  ...params.InputProps,
                  style: undefined, // Remove style to avoid error
                }}
              />
            )}
            slotProps={{
              paper: {
                sx: {
                  backgroundColor: theme.backgroundColor,
                  color: theme.textColor,
                  border: `1px solid ${theme.borderColor}`
                },
              },
            }}
            PopperComponent={(props) => (
              <Popper {...props} placement="bottom" modifiers={[{ name: 'flip', enabled: false }]} />
            )}
          />
        </div>
        <div style={{ marginBottom: '1rem' }}></div>
        <Label style={{ fontWeight: 'bold' }} for="description">
          Description <span style={{color:'red'}}>*</span>
        </Label>
        <TextField
          id="description"
          label="Details of the change"
          variant="outlined"
          value={description}
          multiline
          rows={2}
          onChange={(e) => {
            const sanitized = sanitizeDescription(e.target.value);
            setDescription(sanitized);
            
            // Update validation errors
            if (sanitized && !isValidDescription(sanitized)) {
              setValidationErrors(prev => ({
                ...prev,
                description: 'Description must be between 10 and 500 characters'
              }));
            } else {
              setValidationErrors(prev => ({
                ...prev,
                description: ''
              }));
            }
          }}
          disabled={!!changeNumber}
          fullWidth
          error={!!validationErrors.description}
          helperText={validationErrors.description || `${description.length}/500 characters`}
        />
      </StyledModalBody>
      <StyledModalFooter style={{ display: 'flex', justifyContent: 'space-between' }}>
        <i><span style={{color:'red'}}>*</span> Denotes that the field is required</i>
        <div>
          <Button
        color="primary"
        onClick={handleSubmit}
        disabled={isSubmitDisabled}
        style={{ marginRight: '0.5rem' }}
          >
        Submit
          </Button>
          <Button color="secondary" onClick={closeModal}>
        Cancel
          </Button>
        </div>
      </StyledModalFooter>
    </div>
  );
};

const StyledModalBody = styled(ModalBody)`
  background-color: ${({ theme }) => theme.modal.backgroundColor};
  color: ${({ theme }) => theme.textColor};
  border-color: ${({ theme }) => theme.borderColor};
`;

const StyledModalFooter = styled(ModalFooter)`
  background-color: ${({ theme }) => theme.modal.backgroundColor};
  color: ${({ theme }) => theme.textColor};
  border-color: ${({ theme }) => theme.borderColor};
`;

const StyledAlert = styled(Alert)`
  color: ${({ theme }) => (theme.type === 'dark' ? 'secondary' : 'primary')};
  border: 1px solid ${({ theme }) => theme.borderColor};
`;

export const StyledModalHeader = styled(ModalHeader)`
  background-color: ${(props) => props.theme.modal.backgroundColor};
  color: ${({ theme }) => theme.textColor};
  border-color: ${({ theme }) => theme.borderColor};

  && .close {
    color: ${({ theme }) => theme.textColor};
    text-shadow: 0 1px 0 ${({ theme }) => theme.modal.backgroundColor};
  }
`;

export default ChangeModal;
